import React, { useState, useEffect, useRef } from 'react';
import { getModels, getDefaultModel } from '../../services/modelService';
import { Spinner } from '../common/Spinner';
import { FiChevronDown, FiSearch, FiCheck, FiFilter, FiX, FiArrowUp, FiArrowDown } from 'react-icons/fi';
import { useTheme } from '../../contexts/ThemeContext';

// Add sort options type
type SortOption = 'name' | 'provider';
type SortDirection = 'asc' | 'desc';

/**
 * Dropdown placement relative to the trigger button.
 * 'bottom-right' (default): drops DOWN, anchored to RIGHT edge → grows leftward.
 * 'top-left': pops UP above the trigger, anchored to LEFT edge → grows rightward.
 *   Use this when the trigger sits at the bottom of the viewport (e.g., compose bars)
 *   AND has space to the right (e.g., persona chat where left margin is tight).
 */
type ModelSelectorPlacement = 'bottom-right' | 'top-left';

interface ModelSelectorProps {
  entityType: string;  // "persona", "session", "agent"
  entityId: string;
  onModelSelect: (modelId: string) => void;
  showCosts?: boolean;
  darkMode?: boolean;
  className?: string;
  compact?: boolean;
  showProviderIcon?: boolean;
  includeGreylisted?: boolean;
  showGreylistIndicator?: boolean;
  /** Where the dropdown panel opens relative to the trigger. Default: 'bottom-right'. */
  placement?: ModelSelectorPlacement;
}

export const ModelSelector: React.FC<ModelSelectorProps> = ({
  entityType,
  entityId,
  onModelSelect,
  showCosts = true,
  darkMode: propDarkMode,
  className = '',
  compact = false,
  showProviderIcon = true,
  includeGreylisted = false,
  showGreylistIndicator = false,
  placement = 'bottom-right'
}) => {
  const { darkMode: contextDarkMode } = useTheme();
  const darkMode = propDarkMode !== undefined ? propDarkMode : contextDarkMode;
  
  const [models, setModels] = useState<any[]>([]);
  const [defaultModel, setDefaultModel] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedModel, setSelectedModel] = useState<string | null>(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [selectedProviders, setSelectedProviders] = useState<string[]>([]);
  const [providerSearchQuery, setProviderSearchQuery] = useState('');
  
  // Add sort state
  const [sortBy, setSortBy] = useState<SortOption>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const filterDropdownRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const modelsData = await getModels(false, includeGreylisted);
        setModels(modelsData);
        
        if (entityType && entityId) {
          const defaultData = await getDefaultModel(entityType, entityId);
          setDefaultModel(defaultData.model);
        }
        
        setError(null);
      } catch (err: any) {
        setError(err.message || 'Failed to fetch models');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [entityType, entityId, includeGreylisted]);
  
  useEffect(() => {
    if (defaultModel && !selectedModel) {
      setSelectedModel(defaultModel.id);
      onModelSelect(defaultModel.id);
    }
  }, [defaultModel, selectedModel, onModelSelect]);
  
  // Reset highlighted index when search query changes
  useEffect(() => {
    setHighlightedIndex(-1);
  }, [searchQuery, selectedProviders, sortBy, sortDirection]);
  
  // Reset provider search query when dropdown is closed
  useEffect(() => {
    if (!showFilterDropdown) {
      setProviderSearchQuery('');
    }
  }, [showFilterDropdown]);
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
        setShowFilterDropdown(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (showDropdown && searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    }
  }, [showDropdown]);
  
  // Scroll highlighted item into view
  useEffect(() => {
    if (highlightedIndex >= 0 && listRef.current) {
      const highlightedElement = listRef.current.children[highlightedIndex] as HTMLElement;
      if (highlightedElement) {
        highlightedElement.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }
  }, [highlightedIndex]);
  
  const handleModelChange = (modelId: string) => {
    setSelectedModel(modelId);
    onModelSelect(modelId);
    setShowDropdown(false);
    setSearchQuery('');
  };
  
  // Format model name for display
  const formatModelName = (modelId: string) => {
    if (!modelId) return 'Unknown Model';
    
    const model = models.find(m => m.id === modelId);
    if (model?.name) return model.name;
    
    const { provider, modelName } = parseModelId(modelId);
    return modelName.replace(/-/g, ' ').replace(/(^|\s)\S/g, l => l.toUpperCase());
  };
  
  // Parse model ID into provider and model name
  const parseModelId = (modelId: string) => {
    const parts = modelId.split('/');
    const provider = parts.length > 1 ? parts[0] : 'unknown';
    const modelName = parts.length > 1 ? parts[1] : modelId;
    return { provider, modelName };
  };
  
  // Get provider icon based on provider name
  const getProviderIcon = (provider: string) => {
    switch (provider.toLowerCase()) {
      case 'openai':
        return (
          <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" className="opacity-80">
            <path d="M22.2819 9.8211a5.9847 5.9847 0 0 0-.5157-4.9108 6.0462 6.0462 0 0 0-6.5098-2.9A6.0651 6.0651 0 0 0 4.9807 4.1818a5.9847 5.9847 0 0 0-3.9977 2.9 6.0462 6.0462 0 0 0 .7427 7.0966 5.98 5.98 0 0 0 .511 4.9107 6.051 6.051 0 0 0 6.5146 2.9001A5.9847 5.9847 0 0 0 13.2599 24a6.0557 6.0557 0 0 0 5.7718-4.2058 5.9894 5.9894 0 0 0 3.9977-2.9001 6.0557 6.0557 0 0 0-.7475-7.0729zm-9.022 12.6081a4.4755 4.4755 0 0 1-2.8764-1.0408l.1419-.0804 4.7783-2.7582a.7948.7948 0 0 0 .3927-.6813v-6.7369l2.02 1.1686a.071.071 0 0 1 .038.052v5.5826a4.504 4.504 0 0 1-4.4945 4.4944zm-9.6607-4.1254a4.4708 4.4708 0 0 1-.5346-3.0137l.142.0852 4.783 2.7582a.7712.7712 0 0 0 .7806 0l5.8428-3.3685v2.3324a.0804.0804 0 0 1-.0332.0615L9.74 19.9502a4.4992 4.4992 0 0 1-6.1408-1.6464zM2.3408 7.8956a4.485 4.485 0 0 1 2.3655-1.9728V11.6a.7664.7664 0 0 0 .3879.6765l5.8144 3.3543-2.0201 1.1685a.0757.0757 0 0 1-.071 0l-4.8303-2.7865A4.504 4.504 0 0 1 2.3408 7.872zm16.5963 3.8558L13.1038 8.364 15.1192 7.2a.0757.0757 0 0 1 .071 0l4.8303 2.7913a4.4944 4.4944 0 0 1-.6765 8.1042v-5.6772a.79.79 0 0 0-.407-.667zm2.0107-3.0231l-.142-.0852-4.7735-2.7818a.7759.7759 0 0 0-.7854 0L9.409 9.2297V6.8974a.0662.0662 0 0 1 .0284-.0615l4.8303-2.7866a4.4992 4.4992 0 0 1 6.6802 4.66zM8.3065 12.863l-2.02-1.1638a.0804.0804 0 0 1-.038-.0567V6.0742a4.4992 4.4992 0 0 1 7.3757-3.4537l-.142.0805L8.704 5.459a.7948.7948 0 0 0-.3927.6813zm1.0976-2.3654l2.602-1.4998 2.6069 1.4998v2.9994l-2.5974 1.4997-2.6067-1.4997Z" />
          </svg>
        );
      case 'anthropic':
        return (
          <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" className="opacity-80">
            <path d="M12 2L2 12l10 10 10-10L12 2zm0 2.828l7.172 7.172L12 19.172 4.828 12 12 4.828z" />
          </svg>
        );
      case 'google':
        return (
          <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" className="opacity-80">
            <path d="M12.48 10.92v3.28h7.84c-.24 1.84-.853 3.187-1.787 4.133-1.147 1.147-2.933 2.4-6.053 2.4-4.827 0-8.6-3.893-8.6-8.72s3.773-8.72 8.6-8.72c2.6 0 4.507 1.027 5.907 2.347l2.307-2.307C18.747 1.44 16.133 0 12.48 0 5.867 0 .307 5.387.307 12s5.56 12 12.173 12c3.573 0 6.267-1.173 8.373-3.36 2.16-2.16 2.84-5.213 2.84-7.667 0-.76-.053-1.467-.173-2.053H12.48z" />
          </svg>
        );
      case 'ollama':
        return (
          <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" className="opacity-80">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-2-8c0-1.1.9-2 2-2s2 .9 2 2-.9 2-2 2-2-.9-2-2z" />
          </svg>
        );
      default:
        return (
          <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" className="opacity-80">
            <path d="M21 10.975V8a2 2 0 0 0-2-2h-6V4.688c.305-.274.5-.668.5-1.11a1.5 1.5 0 0 0-3 0c0 .442.195.836.5 1.11V6H5a2 2 0 0 0-2 2v2.975A3.5 3.5 0 0 0 0 14.5a3.5 3.5 0 0 0 3 3.45V21a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-3.05a3.5 3.5 0 0 0 3-3.45 3.5 3.5 0 0 0-3-3.525z" />
          </svg>
        );
    }
  };
  
  // Get all available providers
  const availableProviders = [...new Set(models.map(model => {
    const { provider } = parseModelId(model.id);
    return provider;
  }))].sort();
  
  // Toggle provider selection
  const toggleProvider = (provider: string) => {
    setSelectedProviders(prev => {
      if (prev.includes(provider)) {
        return prev.filter(p => p !== provider);
      } else {
        return [...prev, provider];
      }
    });
  };
  
  // Filter models based on search query, selected providers, and greylisting
  const filteredModels = models.filter(model => {
    const { provider } = parseModelId(model.id);
    
    // Filter by provider if any are selected
    if (selectedProviders.length > 0 && !selectedProviders.includes(provider)) {
      return false;
    }
    
    // Filter out greylisted models unless includeGreylisted is true
    if (!includeGreylisted && model.meta_data?.greylisted) {
      return false;
    }
    
    // Filter by search query
    if (!searchQuery.trim()) return true;
    
    const query = searchQuery.toLowerCase();
    const modelName = formatModelName(model.id).toLowerCase();
    
    return (
      modelName.includes(query) || 
      provider.toLowerCase().includes(query) ||
      (model.description && model.description.toLowerCase().includes(query)) ||
      (model.tags && model.tags.some((tag: string) => tag.toLowerCase().includes(query)))
    );
  });

  // Sort the filtered models
  const sortedModels = [...filteredModels].sort((a, b) => {
    if (sortBy === 'name') {
      const nameA = formatModelName(a.id).toLowerCase();
      const nameB = formatModelName(b.id).toLowerCase();
      
      if (sortDirection === 'asc') {
        return nameA.localeCompare(nameB);
      } else {
        return nameB.localeCompare(nameA);
      }
    } else if (sortBy === 'provider') {
      const providerA = parseModelId(a.id).provider.toLowerCase();
      const providerB = parseModelId(b.id).provider.toLowerCase();
      
      if (sortDirection === 'asc') {
        return providerA.localeCompare(providerB);
      } else {
        return providerB.localeCompare(providerA);
      }
    }
    
    return 0;
  });

  // Toggle sort direction or change sort field
  const toggleSort = (field: SortOption) => {
    if (sortBy === field) {
      // Toggle direction if same field
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new field and default to ascending
      setSortBy(field);
      setSortDirection('asc');
    }
  };

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev < sortedModels.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => prev > 0 ? prev - 1 : prev);
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < sortedModels.length) {
          handleModelChange(sortedModels[highlightedIndex].id);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setShowDropdown(false);
        break;
      case 'Tab':
        setShowDropdown(false);
        break;
    }
  };

  if (loading) return (
    <div className={`flex justify-center items-center ${className}`}>
      <Spinner size="md" color={darkMode ? "#3B82F6" : "#2563EB"} />
    </div>
  );
  
  if (error) return (
    <div className={`text-red-500 p-2 text-sm ${className}`}>
      Error loading models: {error}
    </div>
  );
  
  // Get current model info
  const currentModel = selectedModel 
    ? models.find(m => m.id === selectedModel) 
    : null;
  
  const { provider: currentProvider } = currentModel 
    ? parseModelId(currentModel.id) 
    : { provider: '' };
  
  const currentProviderIcon = showProviderIcon && currentProvider 
    ? getProviderIcon(currentProvider) 
    : null;

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Selector Button */}
      <button
        onClick={() => setShowDropdown(!showDropdown)}
        className={`flex items-center ${
          compact 
            ? 'px-3 py-1.5 text-sm rounded-md' 
            : 'px-4 py-2 text-sm rounded-lg'
        } ${
          darkMode 
            ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' 
            : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
        } transition-colors`}
        aria-expanded={showDropdown}
        aria-haspopup="listbox"
      >
        {currentProviderIcon && (
          <span className="mr-2">{currentProviderIcon}</span>
        )}
        <span className="mr-1">{selectedModel ? formatModelName(selectedModel) : 'Select Model'}</span>
        <FiChevronDown size={14} />
      </button>
      
      {/* Dropdown Menu */}
      {showDropdown && (
        <div
          className={`absolute ${
            placement === 'top-left'
              ? 'left-0 bottom-full mb-1'  // open upward, anchor at left edge → grows rightward
              : 'right-0 mt-1'              // default: drop down, anchor at right edge → grows leftward
          } ${
            compact ? 'w-64' : 'w-72'
          } rounded-lg shadow-lg z-50 overflow-hidden ${
            darkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'
          }`}
          onKeyDown={handleKeyDown}
        >
          {/* Search and filter bar */}
          <div className={`p-2 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
            <div className="flex gap-2">
              {/* Search input */}
              <div className={`flex items-center px-3 py-2 rounded-md flex-grow ${
                darkMode ? 'bg-gray-700' : 'bg-gray-100'
              }`}>
                <FiSearch className={darkMode ? 'text-gray-400' : 'text-gray-500'} />
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder="Search models..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className={`ml-2 bg-transparent border-none focus:outline-none w-full ${
                    darkMode ? 'text-white placeholder-gray-500' : 'text-gray-900 placeholder-gray-400'
                  }`}
                />
              </div>
              
              {/* Filter button */}
              <div className="relative" ref={filterDropdownRef}>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowFilterDropdown(!showFilterDropdown);
                  }}
                  className={`flex items-center justify-center px-3 py-2 rounded-md ${
                    darkMode 
                      ? selectedProviders.length > 0 
                        ? 'bg-blue-600 text-white' 
                        : 'bg-gray-700 text-gray-300 hover:bg-gray-600' 
                      : selectedProviders.length > 0 
                        ? 'bg-blue-100 text-blue-800' 
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                  aria-label="Filter models"
                >
                  <FiFilter size={16} />
                  {selectedProviders.length > 0 && (
                    <span className={`ml-1 text-xs font-medium ${
                      darkMode ? 'text-blue-200' : 'text-blue-700'
                    }`}>
                      {selectedProviders.length}
                    </span>
                  )}
                </button>
                
                {/* Provider filter dropdown */}
                {showFilterDropdown && (
                  <div className={`absolute right-0 mt-1 w-48 rounded-md shadow-lg z-50 overflow-hidden ${
                    darkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'
                  }`}>
                    <div className={`p-2 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                      <div className="flex justify-between items-center mb-2">
                        <span className={`text-xs font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                          Filter by Provider
                        </span>
                        {selectedProviders.length > 0 && (
                          <button
                            onClick={() => setSelectedProviders([])}
                            className={`text-xs ${darkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-800'}`}
                          >
                            Clear All
                          </button>
                        )}
                      </div>
                      
                      {/* Provider search input */}
                      <div className={`flex items-center px-2 py-1 rounded-md ${
                        darkMode ? 'bg-gray-700' : 'bg-gray-100'
                      } mb-2`}>
                        <FiSearch className={darkMode ? 'text-gray-400' : 'text-gray-500'} size={12} />
                        <input
                          type="text"
                          placeholder="Search providers..."
                          value={providerSearchQuery}
                          onChange={(e) => setProviderSearchQuery(e.target.value)}
                          className={`ml-2 bg-transparent border-none focus:outline-none w-full text-xs ${
                            darkMode ? 'text-white placeholder-gray-500' : 'text-gray-900 placeholder-gray-400'
                          }`}
                        />
                      </div>
                    </div>
                    
                    <div className="max-h-48 overflow-y-auto custom-scrollbar">
                      {/* Filter providers based on search query */}
                      {availableProviders
                        .filter(provider => 
                          provider.toLowerCase().includes(providerSearchQuery.toLowerCase())
                        )
                        .map(provider => (
                          <div 
                            key={provider}
                            className={`flex items-center px-3 py-2 ${
                              darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                            }`}
                          >
                            <input
                              type="checkbox"
                              id={`provider-${provider}`}
                              checked={selectedProviders.includes(provider)}
                              onChange={() => toggleProvider(provider)}
                              className={`h-4 w-4 rounded ${
                                darkMode 
                                  ? 'bg-gray-700 border-gray-600 text-blue-600 focus:ring-blue-500' 
                                  : 'bg-gray-100 border-gray-300 text-blue-600 focus:ring-blue-500'
                              }`}
                            />
                            <label 
                              htmlFor={`provider-${provider}`}
                              className={`ml-2 flex items-center text-sm ${
                                darkMode ? 'text-gray-300' : 'text-gray-700'
                              }`}
                            >
                              {showProviderIcon && (
                                <span className="mr-2">{getProviderIcon(provider)}</span>
                              )}
                              {provider.charAt(0).toUpperCase() + provider.slice(1)}
                            </label>
                          </div>
                        ))}
                        
                      {/* Show message when no providers match the search */}
                      {availableProviders.filter(provider => 
                        provider.toLowerCase().includes(providerSearchQuery.toLowerCase())
                      ).length === 0 && (
                        <div className={`px-3 py-2 text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'} text-center`}>
                          No providers found
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            {/* Active filters display */}
            {selectedProviders.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                {selectedProviders.map(provider => (
                  <div 
                    key={provider}
                    className={`flex items-center px-2 py-1 text-xs rounded-full ${
                      darkMode 
                        ? 'bg-blue-900 bg-opacity-50 text-blue-300' 
                        : 'bg-blue-100 text-blue-800'
                    }`}
                  >
                    {provider}
                    <button
                      onClick={() => toggleProvider(provider)}
                      className="ml-1 focus:outline-none"
                      aria-label={`Remove ${provider} filter`}
                    >
                      <FiX size={12} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          {/* Sort controls */}
          <div className={`px-3 py-2 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'} flex justify-between`}>
            <div className="flex items-center">
              <span className={`text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'} mr-2`}>
                Sort by:
              </span>
              <button
                onClick={() => toggleSort('name')}
                className={`flex items-center px-2 py-1 rounded text-xs mr-2 ${
                  sortBy === 'name'
                    ? darkMode 
                      ? 'bg-blue-600 text-white' 
                      : 'bg-blue-100 text-blue-800'
                    : darkMode
                      ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' 
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Name
                {sortBy === 'name' && (
                  sortDirection === 'asc' 
                    ? <FiArrowUp className="ml-1" size={12} /> 
                    : <FiArrowDown className="ml-1" size={12} />
                )}
              </button>
              <button
                onClick={() => toggleSort('provider')}
                className={`flex items-center px-2 py-1 rounded text-xs ${
                  sortBy === 'provider'
                    ? darkMode 
                      ? 'bg-blue-600 text-white' 
                      : 'bg-blue-100 text-blue-800'
                    : darkMode
                      ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' 
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Provider
                {sortBy === 'provider' && (
                  sortDirection === 'asc' 
                    ? <FiArrowUp className="ml-1" size={12} /> 
                    : <FiArrowDown className="ml-1" size={12} />
                )}
              </button>
            </div>
            <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              {sortedModels.length} models
            </div>
          </div>
          
          {/* Models list with custom scrollbar */}
          <div 
            ref={listRef}
            className="max-h-60 overflow-y-auto py-1 custom-scrollbar"
            style={{
              scrollbarWidth: 'thin',
              scrollbarColor: darkMode ? '#4B5563 #1F2937' : '#D1D5DB #F3F4F6'
            }}
          >
            {sortedModels.length > 0 ? (
              sortedModels.map((model, index) => {
                const isSelected = model.id === selectedModel;
                const isHighlighted = index === highlightedIndex;
                const { provider } = parseModelId(model.id);
                const providerIcon = showProviderIcon ? getProviderIcon(provider) : null;
                
                return (
                  <button
                    key={model.id}
                    onClick={() => handleModelChange(model.id)}
                    onMouseEnter={() => setHighlightedIndex(index)}
                    className={`flex items-center w-full text-left px-4 py-2 ${
                      isSelected
                        ? darkMode 
                          ? 'bg-blue-600 text-white' 
                          : 'bg-blue-100 text-blue-800'
                        : isHighlighted
                          ? darkMode
                            ? 'bg-gray-700 text-gray-200'
                            : 'bg-gray-100 text-gray-800'
                          : darkMode
                            ? 'text-gray-300 hover:bg-gray-700' 
                            : 'text-gray-700 hover:bg-gray-100'
                    }`}
                    role="option"
                    aria-selected={isSelected}
                  >
                    {providerIcon && (
                      <div className="flex-shrink-0 mr-3">
                        {providerIcon}
                      </div>
                    )}
                    <div className="flex-grow">
                      <div className="font-medium">{formatModelName(model.id)}</div>
                      <div className={`text-xs ${
                        isSelected
                          ? darkMode ? 'text-blue-200' : 'text-blue-600'
                          : darkMode ? 'text-gray-400' : 'text-gray-500'
                      }`}>
                        {provider}
                      </div>
                      
                      {showCosts && (
                        <div className="mt-1 text-xs grid grid-cols-2 gap-1">
                          <div className={isSelected
                            ? darkMode ? 'text-blue-200' : 'text-blue-600'
                            : darkMode ? 'text-gray-400' : 'text-gray-500'
                          }>
                            Input: ${model.prompt_cost_dollars.toFixed(4)}/1K
                          </div>
                          <div className={isSelected
                            ? darkMode ? 'text-blue-200' : 'text-blue-600'
                            : darkMode ? 'text-gray-400' : 'text-gray-500'
                          }>
                            Output: ${model.completion_cost_dollars.toFixed(4)}/1K
                          </div>
                        </div>
                      )}
                      
                      {model.tags && model.tags.length > 0 && (
                        <div className="mt-1 flex flex-wrap gap-1">
                          {model.tags.slice(0, 3).map((tag: string, idx: number) => (
                            <span 
                              key={idx}
                              className={`text-xs px-1.5 py-0.5 rounded ${
                                isSelected
                                  ? darkMode ? 'bg-blue-500 text-white' : 'bg-blue-200 text-blue-800'
                                  : darkMode ? 'bg-gray-600 text-gray-300' : 'bg-gray-200 text-gray-700'
                              }`}
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}
                      
                      {/* Greylisted indicator */}
                      {showGreylistIndicator && model.meta_data?.greylisted && (
                        <div className="mt-1">
                          <span 
                            className={`text-xs px-1.5 py-0.5 rounded ${
                              isSelected
                                ? darkMode ? 'bg-yellow-500 text-white' : 'bg-yellow-200 text-yellow-800'
                                : darkMode ? 'bg-yellow-600 text-yellow-300' : 'bg-yellow-100 text-yellow-700'
                            }`}
                            title={`This model exceeds cost thresholds: ${
                              model.meta_data.greylisted.prompt_cost_exceeds ? 'Prompt cost too high' : ''
                            }${
                              model.meta_data.greylisted.prompt_cost_exceeds && model.meta_data.greylisted.completion_cost_exceeds ? ', ' : ''
                            }${
                              model.meta_data.greylisted.completion_cost_exceeds ? 'Completion cost too high' : ''
                            }`}
                          >
                            Cost-restricted
                          </span>
                        </div>
                      )}
                    </div>
                    {isSelected && (
                      <div className="flex-shrink-0 ml-2">
                        <FiCheck className={darkMode ? 'text-white' : 'text-blue-600'} />
                      </div>
                    )}
                  </button>
                );
              })
            ) : (
              <div className={`px-4 py-3 text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'} text-center`}>
                No models found matching your criteria
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Add custom scrollbar styles */}
      <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        
        .custom-scrollbar::-webkit-scrollbar-track {
          background: ${darkMode ? '#1F2937' : '#F3F4F6'};
          border-radius: 3px;
        }
        
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background-color: ${darkMode ? '#4B5563' : '#D1D5DB'};
          border-radius: 3px;
        }
        
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background-color: ${darkMode ? '#6B7280' : '#9CA3AF'};
        }
      `}</style>
    </div>
  );
};
